/* /verify-email?token= — confirms the address, signs the owner in and opens the dashboard.
 * Invalid / expired / used links show a resend form. */
import "../account/account.css";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { homeFor, useSession } from "../../app/session";
import { useData } from "../../data";
import { ApiError } from "../../data/provider";
import { useI18n } from "../../i18n";
import { Alert, Button, Icon, Skeleton, TextField, errMsg, useToast } from "../../ui";
import { AuthSide, AuthTop } from "./components/AuthLayout";
import CheckInbox from "./components/CheckInbox";

export default function VerifyEmail() {
  const { L } = useI18n();
  const p = useData();
  const { adopt } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const [state, setState] = useState<"verifying" | "error">(token ? "verifying" : "error");
  const [err, setErr] = useState<string>("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    p.verifyEmail(token).then((r) => {
      adopt(r);
      toast(L("Email confirmed — welcome to StartHR!", "تم تأكيد بريدك — مرحبًا بك في ستارت HR!"));
      nav(homeFor(r.user.role), { replace: true });
    }).catch((e: unknown) => {
      setErr(e instanceof ApiError && e.status === 422 ? L("This confirmation link is invalid, already used or expired.", "رابط التأكيد غير صالح أو مستخدم أو منتهي الصلاحية.") : errMsg(e));
      setState("error");
    });
  }, [token, p, adopt, nav, toast, L]);

  const resend = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setErr(L("Enter a valid email address", "أدخل بريدًا إلكترونيًا صحيحًا")); return; }
    try { await p.resendVerification(email.trim()); setSent(email.trim()); }
    catch (e) { setErr(e instanceof ApiError && e.status === 429 ? L("Too many requests — please wait a few minutes.", "طلبات كثيرة — انتظر بضع دقائق.") : errMsg(e)); }
  };

  return (
    <div className="auth" data-testid="verify-email">
      <AuthSide title={L("One click to get started.", "نقرة واحدة للبدء.")} text={L("Confirming your email protects your company's payroll and employee data.", "تأكيد بريدك يحمي بيانات الرواتب والموظفين في منشأتك.")} />
      <main className="auth-main no-tabbar">
        <AuthTop />
        <div className="auth-card">
          {sent ? <CheckInbox email={sent} onBack={() => setSent(null)} /> : state === "verifying" ? (
            <div className="stack" data-testid="verify-busy">
              <div className="inbox-hero"><span className="ico"><Icon name="shield" /></span><h1>{L("Confirming your email…", "جارٍ تأكيد بريدك…")}</h1></div>
              <Skeleton />
            </div>
          ) : (
            <div className="stack">
              <div className="inbox-hero"><span className="ico"><Icon name="alert" /></span><h1>{L("Link not valid", "الرابط غير صالح")}</h1></div>
              <Alert tone="bad" title={err || L("This confirmation link is missing its token.", "رابط التأكيد ناقص.")} testId="verify-error" />
              <form className="stack" onSubmit={resend} noValidate>
                <TextField label={L("Your email", "بريدك الإلكتروني")} value={email} onChange={setEmail} type="email" dir="ltr" testId="verify-resend-email" />
                <Button type="submit" variant="pri" icon="mail" data-testid="verify-resend">{L("Send a new link", "إرسال رابط جديد")}</Button>
              </form>
              <p className="sm muted" style={{ textAlign: "center", margin: 0 }}><Link to="/login">{L("Back to sign in", "العودة لتسجيل الدخول")}</Link></p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
